/**
 * Theme hook - Zustand-based implementation
 *
 * This hook provides theme management using the centralized Zustand store.
 * The actual theme application happens in two places:
 * 1. index.html inline script - applies theme BEFORE React hydrates (prevents FOUC)
 * 2. appStore.setTheme() - applies theme changes at runtime
 *
 * This architecture ensures:
 * - No hydration mismatch (initial state matches server-rendered HTML)
 * - No Flash of Unstyled Content (FOUC)
 * - System preference changes are handled reactively
 */

import { useCallback, useEffect } from 'react';
import { useAppStore } from '../stores/appStore';
import type { Theme } from '../stores/appStore';

export type { Theme };

export function useTheme() {
  const theme = useAppStore((state) => state.theme);
  const resolvedTheme = useAppStore((state) => state.resolvedTheme);
  const setThemeAction = useAppStore((state) => state.setTheme);
  const initializeTheme = useAppStore((state) => state.initializeTheme);

  // Initialize theme on mount (reads localStorage and sets up listeners)
  useEffect(() => {
    initializeTheme();
  }, [initializeTheme]);

  const setTheme = useCallback(
    (newTheme: Theme) => {
      setThemeAction(newTheme);
    },
    [setThemeAction]
  );

  const toggleTheme = useCallback(() => {
    const newTheme = resolvedTheme === 'light' ? 'dark' : 'light';
    setThemeAction(newTheme);
  }, [resolvedTheme, setThemeAction]);

  return {
    theme,
    resolvedTheme,
    setTheme,
    toggleTheme,
    isDark: resolvedTheme === 'dark',
  };
}
